/**
 * EstimateTemplateSelector Component
 * Allows selecting a template when creating a new estimate
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  FileText, 
  Plus, 
  Search, 
  ChevronRight,
  Sparkles,
  Edit,
  Trash2,
  Copy,
  Clock,
  Star
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useConfirmDialog } from '@/components/ConfirmDialog';

const EstimateTemplateSelector = ({ 
  isOpen, 
  onClose, 
  onSelectTemplate, 
  onStartBlank,
  enabledModes = ['simple', 'detailed']
}) => {
  const { organization, user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  const [isLoading, setIsLoading] = useState(true);
  const [isSeeding, setIsSeeding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    service_type: '',
    display_mode: enabledModes[0] || 'simple',
    summary_sections: [{ title: '', description: '' }],
    line_items: [],
    default_tax_rate: 0,
    default_deposit_type: 'percent',
    default_deposit_value: 0,
    default_valid_days: 30,
    default_scope_of_work: ''
  });

  const serviceTypes = [
    'Concrete', 'Decking', 'Fencing', 'Landscaping', 'Painting',
    'Roofing', 'Plumbing', 'Electrical', 'HVAC', 'Patio',
    'General Construction', 'Cleaning', 'Flooring', 'Other'
  ];

  useEffect(() => {
    if (isOpen && organization?.id) {
      loadTemplates();
    }
  }, [isOpen, organization?.id]);

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await apiHelpers.getEstimateTemplates(organization.id);
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectTemplate = async (template) => {
    try {
      // Track usage
      await apiHelpers.useEstimateTemplate(organization.id, template.id);
      onSelectTemplate(template);
    } catch (error) {
      console.error('Failed to use template:', error);
      // Still proceed even if tracking fails
      onSelectTemplate(template);
    }
  };

  const handleSaveTemplate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    try {
      if (editingTemplate) {
        await apiHelpers.updateEstimateTemplate(
          organization.id,
          editingTemplate.id,
          formData,
          user?.id
        );
        toast.success('Template updated');
      } else {
        await apiHelpers.createEstimateTemplate(organization.id, formData, user?.id);
        toast.success('Template created');
      }
      
      setShowCreateDialog(false);
      setEditingTemplate(null);
      resetForm();
      loadTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    const confirmed = await showConfirm({
      title: 'Delete Template?',
      description: 'This template will be permanently removed.',
      variant: 'danger',
      confirmText: 'Delete',
      destructive: true
    });
    if (!confirmed) return;
    
    try {
      await apiHelpers.deleteEstimateTemplate(organization.id, templateId, user?.id);
      toast.success('Template deleted');
      loadTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      service_type: '',
      display_mode: enabledModes[0] || 'simple',
      summary_sections: [{ title: '', description: '' }],
      line_items: [],
      default_tax_rate: 0,
      default_deposit_type: 'percent',
      default_deposit_value: 0,
      default_valid_days: 30,
      default_scope_of_work: ''
    });
  };

  // Seed default templates based on org's service types
  const handleSeedDefaults = async () => {
    if (!organization?.id) return;
    
    setIsSeeding(true);
    try {
      const response = await apiHelpers.seedDefaultTemplates(organization.id, user?.id);
      const data = response.data;
      
      if (data.templates_created > 0) {
        toast.success(`Created ${data.templates_created} starter templates!`, {
          description: `Ready to use for: ${data.templates.map(t => t.service_type).join(', ')}`
        });
        loadTemplates();
      } else if (data.templates_skipped > 0) {
        toast.info('Default templates already exist', {
          description: 'You can create custom templates or edit existing ones'
        });
      } else {
        toast.info('No service types configured', {
          description: 'Add service types in Settings to generate templates'
        });
      }
    } catch (error) {
      console.error('Failed to seed templates:', error);
      toast.error('Failed to create default templates');
    } finally {
      setIsSeeding(false);
    }
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      service_type: template.service_type,
      line_items: template.line_items || [],
      default_tax_rate: template.default_tax_rate,
      default_valid_days: template.default_valid_days || 30,
      default_scope_of_work: template.default_scope_of_work || ''
    });
    setShowCreateDialog(true);
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.service_type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Start from Template
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Templates Grid */}
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading templates...</div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground mb-4">
                  {searchQuery ? 'No templates match your search' : 'No templates yet'}
                </p>
                
                {/* Show Generate Defaults button when no templates exist */}
                {!searchQuery && organization?.service_types_enabled?.length > 0 && (
                  <div className="space-y-3">
                    <Button
                      onClick={handleSeedDefaults}
                      disabled={isSeeding}
                      className="btn-primary"
                    >
                      {isSeeding ? (
                        <>
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Creating Templates...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate Starter Templates
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Creates templates with industry-standard line items for your services:<br />
                      <span className="font-medium">{organization.service_types_enabled.slice(0, 5).join(', ')}</span>
                      {organization.service_types_enabled.length > 5 && ` +${organization.service_types_enabled.length - 5} more`}
                    </p>
                    <div className="border-t pt-3 mt-3">
                      <p className="text-xs text-muted-foreground mb-2">Or create your own:</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          resetForm();
                          setShowCreateDialog(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Custom Template
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Fallback when no service types */}
                {!searchQuery && (!organization?.service_types_enabled || organization.service_types_enabled.length === 0) && (
                  <div className="space-y-3">
                    <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                      Add service types in Settings to generate starter templates
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        resetForm();
                        setShowCreateDialog(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Custom Template
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid gap-3">
                {filteredTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="border rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50/50 transition-colors cursor-pointer group"
                    onClick={() => handleSelectTemplate(template)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium">{template.name}</h3>
                          {/* Mode badge */}
                          <span className={cn(
                            "text-xs px-2 py-0.5 rounded-full font-medium",
                            template.display_mode === 'simple' ? "bg-emerald-100 text-emerald-700" :
                            template.display_mode === 'detailed' ? "bg-blue-100 text-blue-700" :
                            template.display_mode === 'summary' ? "bg-purple-100 text-purple-700" :
                            template.display_mode === 'sections' ? "bg-orange-100 text-orange-700" :
                            "bg-gray-100 text-gray-700"
                          )}>
                            {template.display_mode === 'simple' ? 'Simple' :
                             template.display_mode === 'detailed' ? 'Itemized' :
                             template.display_mode === 'summary' ? 'Summary' :
                             template.display_mode === 'sections' ? 'Combined' : 'Standard'}
                          </span>
                          {!enabledModes.includes(template.display_mode || 'detailed') && (
                            <span className="text-xs text-amber-600" title="This template's mode is not enabled. It will be converted to your default mode.">
                              (will convert)
                            </span>
                          )}
                          {template.usage_count > 0 && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Used {template.usage_count}x
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {template.service_type}
                          {template.description && ` • ${template.description}`}
                        </p>
                        {template.line_items?.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-2">
                            {template.line_items.length} line item{template.line_items.length > 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDialog(template);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTemplate(template.id);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4 mt-4 flex-row justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  resetForm();
                  setShowCreateDialog(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Template
              </Button>
              {/* Show generate button if some service types might be missing templates */}
              {organization?.service_types_enabled?.length > templates.length && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSeedDefaults}
                  disabled={isSeeding}
                  title="Generate templates for service types that don't have one"
                >
                  {isSeeding ? (
                    <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                </Button>
              )}
            </div>
            <Button onClick={onStartBlank}>
              Start Blank Estimate
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Template Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Template Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Standard Driveway Repair"
              />
            </div>

            <div>
              <Label>Service Type *</Label>
              <select
                value={formData.service_type}
                onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                className="input-default w-full"
              >
                <option value="">Select service type...</option>
                {serviceTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this template"
              />
            </div>

            <div>
              <Label>Default Scope of Work</Label>
              <Textarea
                value={formData.default_scope_of_work}
                onChange={(e) => setFormData({ ...formData, default_scope_of_work: e.target.value })}
                placeholder="Pre-filled scope of work for this template..."
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valid Days</Label>
                <Input
                  type="number"
                  value={formData.default_valid_days}
                  onChange={(e) => setFormData({ ...formData, default_valid_days: parseInt(e.target.value) || 30 })}
                />
              </div>
              <div>
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.default_tax_rate || ''}
                  onChange={(e) => setFormData({ ...formData, default_tax_rate: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder="Use org default"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateDialog(false);
              setEditingTemplate(null);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate} className="btn-primary">
              {editingTemplate ? 'Save Changes' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    {ConfirmDialogComponent}
    </>
  );
};

export default EstimateTemplateSelector;
